import { SERVER_URL } from '../../constants.js';

// Low-level create/update call.
// Resolves to { ok }              on success,
//             { ok: false, duplicate: true, existing }  when the backend rejects an
//                                            identical payment with 409 (existing is the
//                                            PaymentDTO of the row already on file),
//             { ok: false }       on any other failure.
// Pass { confirmDuplicate: true } to override the duplicate guard on a re-send.
export const addOrUpdatePayment = (studentId, payment, { confirmDuplicate = false } = {}) => {
  const token = sessionStorage.getItem("bearer");
  const isUpdate = !!payment.id;

  let url = isUpdate
    ? `${SERVER_URL}api/payments/${payment.id}` // PUT to specific payment
    : `${SERVER_URL}api/payments/${studentId}`; // POST new for student

  if (!isUpdate && confirmDuplicate) {
    url += `?confirmDuplicate=true`;
  }

  return fetch(url, {
    method: isUpdate ? 'PUT' : 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(payment)
  })
  .then(async response => {
    if (response.status === 409) {
      // Backend's duplicate guard: an identical payment already exists.
      const existing = await response.json().catch(() => null);
      return { ok: false, duplicate: true, existing };
    }
    if (!response.ok) {
      console.error(response.statusText);
      return { ok: false };
    }
    return { ok: true };
  })
  .catch(err => {
    console.error(err);
    return { ok: false, error: err };
  });
};

// Submit a payment, transparently handling the backend's duplicate guard.
// On a 409 the user is asked to confirm; if they accept, the payment is re-sent
// with confirmDuplicate=true. Resolves to { ok, cancelled } where `cancelled`
// means the user declined to create the duplicate (callers can keep the form open).
export const submitPaymentWithDuplicateGuard = (studentId, payment) => {
  return addOrUpdatePayment(studentId, payment).then(result => {
    if (!result.duplicate) {
      return { ok: result.ok, cancelled: false };
    }

    const existing = result.existing;
    const detail = existing
      ? `£${existing.paymentamount} via ${existing.paymentaccount} on ${existing.paymentdate} (id ${existing.id})`
      : 'an identical payment';

    const confirmed = window.confirm(
      `An identical payment already exists for this student:\n  ${detail}\n\n` +
      `Are you sure you want to record another one?`
    );

    if (!confirmed) {
      return { ok: false, cancelled: true };
    }

    return addOrUpdatePayment(studentId, payment, { confirmDuplicate: true })
      .then(retry => ({ ok: retry.ok, cancelled: false }));
  });
};
